import { useTranslation } from "react-i18next";
import { PlanBadge } from "./PlanBadge";
import type { Account } from "../types";

export interface LicenseUsageMeterProps {
  account: Account | null;
  currentUserIsTeamOwnerOrAdmin: boolean;
  isPaidTeamPlan: boolean;
  isUsedLicenseGreaterThanQuota: boolean;
}

export function LicenseUsageMeter({
  account,
  currentUserIsTeamOwnerOrAdmin,
  isPaidTeamPlan,
  isUsedLicenseGreaterThanQuota,
}: LicenseUsageMeterProps) {
  const { t } = useTranslation();

  const used = Number(account?.numberOfUsedLicenses ?? 0);
  const quota = Number(account?.licenseQuantity ?? 0);
  const shouldShow =
    currentUserIsTeamOwnerOrAdmin && isPaidTeamPlan && Boolean(account?.id) && quota > 0;

  if (!shouldShow) return null;

  const percent = Math.min(100, Math.round((used / quota) * 100));
  const caution = isUsedLicenseGreaterThanQuota || used >= quota;
  const fillColor = caution ? "#FF7747" : "#6A1EBB";

  return (
    // AHA2-21: EXACT clone of the presenter reference
    // (stpancras-presenter-app TeamLicenseUsageMeter.vue @ acc3d51 — the
    // sandbox source the reporter asked us to match to the pixel). The block is
    // a self-contained 40px column: 24px text+badge row + 12px gap + 4px bar,
    // top-aligned, so it centres cleanly against the 40px-tall action buttons.
    //   Vue classes → px:
    //   outer   flex flex-col items-start gap-3          → column, align-start, gap 12
    //   textrow flex h-6 items-center gap-[15px]         → height 24, center, gap 15
    //   text    text-sm leading-6 font-normal tracking-[0.2px] text-black
    //                                                    → 14px / lh 24 / 400 / 0.2px / #000
    //   count   font-semibold                            → 600
    //   bar     h-1 w-full rounded-full  bg #E3E3E3      → height 4, radius full
    //   fill    #6A1EBB (normal) / #FF7747 (over quota)
    <div
      data-testid="team-license-usage-meter"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 12,
      }}
    >
      <div style={{ display: "flex", height: 24, alignItems: "center", gap: 15 }}>
        <p
          data-testid="team-license-usage-meter-text"
          style={{
            margin: 0,
            fontSize: 14,
            fontWeight: 400,
            lineHeight: "24px",
            letterSpacing: "0.2px",
            color: "#000",
            whiteSpace: "nowrap",
          }}
        >
          <span style={{ fontWeight: 600 }}>{used}</span>
          <span>
            /{quota} {t("team.license.usedSuffix")}
          </span>
        </p>
        {/* AHA-43296: enterprise teams badge as "Enterprise", matching the
            member-table rows below. The admin page passes a synthesized
            account stub without `isEnterprise`, so its meter is unaffected
            (admin enterprise display is out of scope). */}
        <PlanBadge
          plan={account?.plan}
          isEnterprise={Boolean(account?.isEnterprise)}
        />
      </div>
      <div
        data-testid="team-license-usage-meter-progress"
        role="progressbar"
        aria-label="licences used"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        style={{
          height: 4,
          width: "100%",
          borderRadius: 9999,
          backgroundColor: "#E3E3E3",
          overflow: "hidden",
        }}
      >
        <div
          data-testid="team-license-usage-meter-progress-fill"
          style={{
            width: `${percent}%`,
            height: "100%",
            backgroundColor: fillColor,
            borderRadius: 9999,
            transition: "width 200ms ease-out",
          }}
        />
      </div>
    </div>
  );
}
